/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable dot-notation */
import React, {
  useEffect,
  useCallback,
  useState,
  useRef,
} from 'react';
import clsx from 'clsx';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Snackbar,
  CircularProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Backup as UploadIcon,
  AccountCircle as AccountCircleIcon,
} from '@material-ui/icons';

import { toBase64, isMobile } from '../../helpers';
import { cameraStart, cameraTrigger } from '../../helpers/camera';

import {
  selectCard,
  selectReservation,
  selectCompanyCode,
  selectReservationCode,
  selectIsPending,
  parsePaymentCard,
  setCompanyCode,
  setReservationCode,
  getPaymentCardSessionByReference,
  getPaymentCardSessionByIdentityReference,
  getReservationById,
} from '../../store/widgetSlice';

import {
  setMessage,
  clearMessage,
  selectMessage,
  selectSeverity,
} from '../../store/authSlice';

import BaseButton from '../common/BaseButton';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%',
  },
  submit: {
    marginBottom: theme.spacing(2),
  },
  loader: {
    display: 'flex',
    position: 'fixed',
    alignItems: 'center',
    justifyContent: 'center',
    top: 0,
    left: 0,
    zIndex: 10000,
    width: '100%',
    height: '100%',
  },
  elementHidden: {
    display: 'none',
  },
  alertContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: ' center',
    marginBottom: 25,
  },
  alertContainerInner: {
    display: 'inline-block',
  },
  alignCenter: {
    textAlign: 'center',
  },
}));

export default function ParsePaymentCardForm({ reference, identityReference }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const message = useSelector(selectMessage);
  const severity = useSelector(selectSeverity);
  const session = useSelector(selectCard);
  const reservation = useSelector(selectReservation);
  const companyCode = useSelector(selectCompanyCode);
  const reservationCode = useSelector(selectReservationCode);
  const isPending = useSelector(selectIsPending);

  const cameraView = useRef();
  const cameraSensor = useRef();
  const cameraOutput = useRef();

  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [cardImage, setCardImage] = useState(null);
  const [parseResponse, setParseResponse] = useState(null);
  const [retry, setRetry] = useState(false);

  useEffect(() => {
    if (cameraView && cameraView.current) {
      cameraStart(cameraView.current);
    }
  }, [dispatch, reference, identityReference]);

  useEffect(() => {
    if (reference) {
      dispatch(getPaymentCardSessionByReference({ reference }))
        .finally(() => {
          setLoading(false);
        });
    } else if (identityReference) {
      dispatch(getPaymentCardSessionByIdentityReference({ reference: identityReference }))
        .finally(() => {
          setLoading(false);
        });
    }
  }, [dispatch, reference, identityReference]);

  useEffect(() => {
    if (session && session.reservationId) {
      dispatch(getReservationById({ reservationId: session.reservationId }));
    }
  }, [dispatch, session]);

  useEffect(() => {
    if (reservation && reservation.companyCode && reservation.reservationCode) {
      dispatch(setCompanyCode(reservation.companyCode));
      dispatch(setReservationCode(reservation.reservationCode));
    }
  }, [dispatch, reservation]);

  const handleClose = () => {
    dispatch(clearMessage());
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleRetry = (event) => {
    if (event) event.preventDefault();
    setRetry(true);
    setParseResponse(null);

    if (cameraOutput && cameraOutput.current) {
      setCardImage(null);

      if (cameraOutput) {
        cameraOutput.current.classList.remove('taken');
      }

      cameraStart(cameraView.current);
    }
  };

  const attemptParsePaymentCard = (event) => {
    if (event) event.preventDefault();
    if (companyCode && reservationCode && cardImage) {
      dispatch(parsePaymentCard({ companyCode, reservationCode, cardImage }))
        .unwrap()
        .then((response) => {
          setRetry(false);
          setParseResponse(response);

          if (response) {
            dispatch(setMessage({ message: 'Session updated successfully', severity: 'success' }));
          } else {
            dispatch(setMessage({ message: 'Session could not be updated', severity: 'error' }));
          }
        })
        .catch(() => {
          handleRetry();
          dispatch(setMessage({ message: 'Session could not be updated', severity: 'error' }));
        });
    }
  };

  const attemptParsePaymentCardCallback = useCallback(
    attemptParsePaymentCard,
    [dispatch, cardImage, companyCode, reservationCode],
  );

  useEffect(() => {
    if (file) {
      toBase64(file)
        .then((data) => {
          const [imageData] = data.split(',').slice(-1) || [];

          setPreviewImage(data);
          setCardImage(imageData);
        })
        .catch(() => {
          dispatch(setMessage({ message: 'File could not be parsed', severity: 'error' }));
        });
    }
  }, [dispatch, file]);

  useEffect(() => {
    if (cameraOutput && cameraOutput.current) {
      cameraOutput.current.addEventListener('load', () => {
        const data = cameraOutput.current.src;
        const [imageData] = data.split(',').slice(-1) || [];

        // setPreviewImage(data);
        setCardImage(imageData);
      });
    }
  }, [dispatch, cameraOutput]);

  useEffect(() => {
    if (cameraOutput && cameraOutput.current && cardImage) {
      attemptParsePaymentCardCallback();
    }
  }, [dispatch, attemptParsePaymentCardCallback, cameraOutput, cardImage]);

  return (
    <>
      <Snackbar open={!!message} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity={severity}>{message}</Alert>
      </Snackbar>
      {
        (isPending)
          ? (
            <div className={classes.loader}>
              <CircularProgress size={100} />
            </div>
          )
          : <></>
      }
      <div
        className={clsx(
          ((!session || (!retry && ((session && (session.status !== 'P')) || parseResponse))))
            ? classes.elementHidden
            : '',
        )}
      >
        { (!isMobile())
          ? (
            <div className="desktop-file-upload">
              <h1>Credit Card Verification</h1>
              <form className={classes.form} noValidate>
                <div className="file-container">
                  <label className="file-label" htmlFor="file">
                    <div className="file-box">
                      <Box className="preview-box">
                        {(previewImage)
                          ? <img className="preview-image" src={previewImage} alt="Preview" />
                          : ''}
                        <div className="preview-placeholder">
                          <UploadIcon className="preview-icon" />
                          <p className="preview-text">Please upload a clear picture of your payment card.</p>
                        </div>
                      </Box>
                    </div>
                  </label>
                  <input
                    className="file-input"
                    type="file"
                    name="file"
                    id="file"
                    placeholder={null}
                    onChange={handleFileChange}
                  />
                </div>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  onClick={attemptParsePaymentCard}
                  disabled={isPending || !cardImage || !companyCode || !reservationCode}
                >
                  Submit
                </Button>
              </form>
            </div>
          )
          : (
            <>
              <div className="camera">
                <canvas className="camera__sensor" ref={cameraSensor} />

                <div className="camera__message-container">
                  <p className="camera__message">
                    {
                    (!cardImage || !companyCode || !reservationCode)
                      ? 'Please take a clear picture of your payment card within the border guide and without glare.'
                      : 'Processing card...'
                  }
                  </p>
                </div>

                <div className="camera__container">
                  <video className="camera__view" autoPlay playsInline ref={cameraView} />
                  <div className="camera__overlay" />
                </div>

                <img src="//:0" alt="" className="camera__output" ref={cameraOutput} />

                <div className="camera__controls">
                  <button
                    type="button"
                    onClick={() => cameraTrigger(cameraView.current, cameraSensor.current, cameraOutput.current)}
                    className={clsx((!(!cardImage || !companyCode || !reservationCode)) ? classes.elementHidden : '')}
                  >
                    Take a picture
                  </button>

                  <button
                    type="button"
                    onClick={handleRetry}
                    disabled={isPending || !cardImage || !companyCode || !reservationCode}
                    // eslint-disable-next-line max-len
                    className={clsx((!cardImage || !companyCode || !reservationCode || isPending) ? classes.elementHidden : '')}
                  >
                    Retry
                  </button>
                </div>
                <div className="camera__disclaimer">
                  <p className="camera__disclaimer-text">
                    Your information is encrypted and your card number covered.
                  </p>
                </div>
              </div>
            </>
          )}
      </div>
      <div
        className={clsx(
          (!session
          || (retry || (!(session && (session.status !== 'P'))
          || parseResponse)))
            ? classes.elementHidden
            : '',
        )}
      >
        <h1 className={classes.alignCenter}>Card Verification Results</h1>
        <div className={classes.alertContainer}>
          <div className={classes.alertContainerInner}>
            {
              // eslint-disable-next-line no-nested-ternary
              session && (!session.cardNumber || session.attempts === 3)
                ? (session.attempts === 3)
                  ? (
                    <Alert severity="error">
                      Max number of attempts was reached.
                      Please reach out to your property manager.
                    </Alert>
                  )
                  : (
                    <Alert severity="error">
                      It looks like the image wasn&apos;t taken properly.
                      Please retake the photo by clicking on the &quot;retry&quot; button. Thank you!
                    </Alert>
                  )
                : (
                  <Alert severity="success">
                    You have successfully completed card verification process, you may now close this window.
                  </Alert>
                )
            }
          </div>
        </div>
        { session && session.status !== 'C' && session.attempts < 3
          && (
          <>
            <div className={classes.alignCenter}>
              <BaseButton
                IconComponent={AccountCircleIcon}
                onClick={handleRetry}
              >
                Retry Verification
              </BaseButton>
            </div>
            <br />
          </>
          )}
      </div>
      <div
        className={clsx(
          (!session || (retry || !parseResponse))
            ? classes.elementHidden
            : '',
        )}
      >
        <h1 className={classes.alignCenter}>Card Verification Results</h1>
        <div className={classes.alertContainer}>
          <div className={classes.alertContainerInner}>
            {
              parseResponse && !parseResponse.cardNumber
                ? (
                  <Alert severity="error">
                    It looks like the image wasn&apos;t taken properly.
                    Please retake the photo by clicking on the &quot;retry&quot; button. Thank you!
                  </Alert>
                )
                : (
                  <Alert severity="success">
                    You have successfully completed card verification process, you may now close this window.
                  </Alert>
                )
            }
          </div>
        </div>
        { parseResponse && parseResponse.status !== 'C' && parseResponse.attempts < 3
          && (
          <>
            <div className={classes.alignCenter}>
              <BaseButton
                IconComponent={AccountCircleIcon}
                onClick={handleRetry}
              >
                Retry Verification
              </BaseButton>
            </div>
            <br />
          </>
          )}
      </div>
      {
        (!loading && !session && !parseResponse)
        && (
        <div>
          <h1 className={classes.alignCenter}>Verification Success</h1>
          <div className={classes.alertContainer}>
            <div className={classes.alertContainerInner}>
              <Alert severity="success">
                You have successfully completed verification process, you may now close this window.
              </Alert>
            </div>
          </div>
        </div>
        )
      }
    </>
  );
}
